import { createImmerContext } from './createImmerContext';

export type ScaleRef = {
  min: number;
  setScale: (scale: number) => void;
  frame: { width: number; height: number };
};

/**
 * The editor's global interaction mode (the `printCardEditorModes` flag).
 * - `edit`   — content: every inline-editable text layer is editing at once, and every other
 *              layer's toolbar is reachable. Nothing moves or resizes.
 * - `layout` — geometry: move and resize only. No caret, and no toolbars.
 */
export type EditorMode = 'edit' | 'layout';

export interface SurfaceState {
  scale: number;
  hoverLayerIndex: number | undefined;
  activeLayerIndex: number | undefined;
  editingLayerIndex: number | undefined;
  scaleRef: ScaleRef | undefined;
  /**
   * `undefined` means the mode switcher is off, and interaction stays on the legacy
   * per-layer path (tap to select, tap again to drag or to enter caret editing).
   */
  mode: EditorMode | undefined;
  /**
   * The layer a delete was asked for, awaiting the user's confirmation. Every delete control (the
   * layer toolbar, the mode bar, the Delete key) only asks; `DeleteLayerConfirm` does the deleting.
   */
  pendingDeleteLayerIndex: number | undefined;
  /** The layer just added from the Add menu, pulsed once by `LayerPulse` and then cleared. */
  addedLayerIndex: number | undefined;
  /** Bumped on every press of "Edit" (even when already in it), replaying `Surface`'s intro pulse. */
  introReplayCount: number;
  /**
   * Bumped on every layer delete. Layers are keyed by index, so after a delete every later layer
   * would inherit the React state (caret, measurements) of the one before it; `EditablePage` keys
   * layers on this too, so they remount instead.
   */
  layerDeleteCount: number;
}

const [SurfaceProvider, useSurface] = createImmerContext<SurfaceState>(() => ({
  scale: 1,
  hoverLayerIndex: undefined,
  activeLayerIndex: undefined,
  editingLayerIndex: undefined,
  scaleRef: undefined,
  mode: undefined,
  pendingDeleteLayerIndex: undefined,
  addedLayerIndex: undefined,
  introReplayCount: 0,
  layerDeleteCount: 0,
}));

export { SurfaceProvider, useSurface };
